import { useRef, useState } from "react";
import { GuideCue, GuideCueProps } from "@leafygreen-ui/guide-cue";
import { palette } from "@leafygreen-ui/palette";
import InfoWithCircle from "@via-ds/icons/InfoWithCircle";
import { differenceInDays } from "date-fns";
import Cookies from "js-cookie";
import styles from "./index.module.css";

type Props = {
  activeDays?: number;
  cookieName: string;
  loading?: boolean;
};

/**
 * ExpiringAnnouncementTooltip displays a clickable info icon with a popover.
 * On first view, the popover opens automatically. Once dismissed, a cookie is set
 * and the popover stays closed on future visits. The info icon remains visible
 * for `activeDays` (default 8) after first dismissal, then disappears entirely.
 * @param props - React props
 * @param props.activeDays - number of days the info icon will persist after the tooltip is first viewed
 * @param props.cookieName - cookie associated with the view state
 * @param props.loading - whether data present in the tooltip is loading, helps avoid flickering tooltip
 * @returns React component
 */
export const ExpiringAnnouncementTooltip: React.FC<
  Props & Pick<GuideCueProps, "children" | "title" | "tooltipAlign">
> = ({ activeDays = 8, cookieName, loading = false, ...guideCueProps }) => {
  const now = new Date();
  const infoRef = useRef(null);

  const seenTooltipCookie: string | undefined = Cookies.get(cookieName);
  const neverSeenTooltip = !seenTooltipCookie;
  const seenTooltipDate = seenTooltipCookie ? new Date(seenTooltipCookie) : now;

  const [open, setOpen] = useState(neverSeenTooltip);

  const close = () => {
    setOpen(false);

    if (neverSeenTooltip) {
      Cookies.set(cookieName, seenTooltipDate.toString(), { expires: 365 });
    }
  };

  return !loading &&
    (neverSeenTooltip ||
      differenceInDays(now, seenTooltipDate) < activeDays) ? (
    <>
      <div ref={infoRef} className={styles.iconContainer}>
        <InfoWithCircle
          data-testid="announcement-tooltip-trigger"
          fill={palette.gray.dark2}
          onClick={() => setOpen((o) => !o)}
        />
      </div>
      <GuideCue
        currentStep={1}
        numberOfSteps={1}
        onDismiss={close}
        onPrimaryButtonClick={close}
        open={open}
        refEl={infoRef}
        setOpen={setOpen}
        {...guideCueProps}
      />
    </>
  ) : null;
};
